import EditIcon from "@mui/icons-material/Edit";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  createUpdateClinicBranch,
  getClinicBranches,
  updateClinicBranchStatus,
} from "../../../redux/reducers/clinicSlice";
import { countryList } from "../../../util/arrayList";
import { w20h20BlueIcon } from "../../../util/CommonIconStyle";
import { getKey } from "../../../util/function";
import { patchMandateSession } from "../../../util/mandateSessionStorage";
import { tblGrayBack } from "../../../util/object";
import generatePass from "../../../util/randomPassword";
import { EmailRegex, numberRegx } from "../../../util/Validations";

const tableHeaders = [
  "branchName",
  "location",
  "adminContact",
  "mobile",
  "action",
];

const tableNameExpan = {
  branchName: "Branch Name",
  location: "Location",
  adminContact: "Administrator Name",
  mobile: "Contact Number",
  action: "Action",
};

const intValues = { country: null, state: null };
const requiredFields = [
  "branchName",
  "country",
  "state",
  "address",
  "adminContact",
  "mobile",
  "email",
  "password",
];

const toClinicBranchesList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const ClinicBranches = () => {
  const dispatch = useDispatch();
  const [modVisible, setModVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modValues, setModValues] = useState(intValues);
  const [modErrors, setModErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const { branches } = useSelector((state) => state?.clinic);

  const refreshBranchesAndPatchMandate = () => {
    try {
      dispatch(getClinicBranches()).then((res) => {
        try {
          const payload = res?.payload;
          if (payload == null) return;
          patchMandateSession({
            clinicBranches: toClinicBranchesList(payload),
          });
        } catch {
          // ignore mandate patch errors
        }
      });
    } catch {
      // ignore branches refresh errors
    }
  };

  useEffect(() => {
    refreshBranchesAndPatchMandate();
  }, []);

  const handleChangeSwitch = (val, li) => {
    if (!li?.id) return;
    dispatch(updateClinicBranchStatus({ id: li?.id, status: val })).then(
      (res) => {
        if (res?.payload) refreshBranchesAndPatchMandate();
      },
    );
  };

  const handleEdit = (itm) => {
    const contryName = itm?.country?.split("(")?.[0];
    const countryCode = itm?.country?.substring(
      itm?.country?.lastIndexOf("(") + 1,
      itm?.country?.lastIndexOf(")"),
    );
    const reqObj = {
      branchName: itm?.branchName,
      country: { name: contryName, value: contryName },
      countryCode,
      state: { name: itm?.state, value: itm?.state },
      address: itm?.address,
      adminContact: itm?.adminContact,
      mobile: itm?.mobile,
      email: itm?.email,
      password: itm?.password,
    };
    setModValues(reqObj);
    setSelectedItem(itm);
    setModVisible(true);
  };

  const getTableData = () => {
    return (branches ?? [])?.map((li) => {
      const contryName = li?.country?.split("(")?.[0] ?? "";
      return {
        ...li,
        branchName: li?.branchName,
        location: [li?.address, li?.state, contryName]
          ?.filter(Boolean)
          ?.join(", "),
        adminContact: li?.adminContact,
        mobile: li?.mobile,
        action: (
          <div className="flex-row-ali-cen">
            <div>
              <CustomSwitch
                value={li?.active === "Y"}
                onChange={(e) =>
                  handleChangeSwitch(li?.active === "Y" ? "N" : "Y", li)
                }
                greenToGray
              />
            </div>
            <div className="cursor ml15" onClick={() => handleEdit(li)}>
              <div className="flex-center">
                <EditIcon sx={w20h20BlueIcon} />
              </div>
              <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                Edit
              </Typography>
            </div>
          </div>
        ),
      };
    });
  };

  const onCreateBranch = () => {
    setModValues(intValues);
    setModErrors({});
    setSelectedItem(null);
    setModVisible(true);
  };

  const handleChange = (name, value) => {
    const reqObj = { ...modValues, [name]: value };
    const errorObj = { ...modErrors };
    if (requiredFields.includes(name)) {
      const reqValue = typeof value === "object" ? value?.value : value;
      if (reqValue?.length > 0) {
        errorObj[name] = "";
        if (name === "email") {
          if (!EmailRegex.test(reqValue)) {
            errorObj[name] = "Invalid email";
          }
        }
        if (name === "password") {
          if (reqValue?.length < 6) {
            errorObj[name] = "Password should be at least 6 characters long";
          }
        }
        if (name === "mobile") {
          if (!numberRegx.test(reqValue)) {
            errorObj[name] = "Invalid mobile number";
          } else {
            if (reqValue?.length < 6) {
              errorObj[name] =
                "Mobile number should be at least 6 characters long";
            }
          }
        }
      } else {
        errorObj[name] = "This is required field";
      }
    }
    if (name === "country") {
      reqObj.country = { name: value?.name ?? "", value: value?.value ?? "" };
      reqObj.countryCode = value?.dial_code ?? "";
      reqObj.state = null;
      if (getStatesBasedOnCountry(value?.value)?.length === 0) {
        errorObj.state = "";
      }
    }
    if (name === "isAutoGenPass" && value) {
      reqObj.password = generatePass();
    }
    setModErrors(errorObj);
    setModValues(reqObj);
  };

  const getStatesBasedOnCountry = (country) => {
    return (
      countryList
        ?.find((li) => li?.value?.toLowerCase() === country?.toLowerCase())
        ?.states?.map((li) => ({ name: li?.name, value: li?.name })) ?? []
    );
  };

  const onCloseModal = () => {
    setModVisible(false);
    setModValues(intValues);
    setModErrors({});
    setShowPassword(false);
    setSelectedItem(null);
  };

  const isNotValidValues = () => {
    const errorObj = { ...modErrors };
    requiredFields?.forEach((key) => {
      const reqValue =
        typeof modValues?.[key] === "object"
          ? modValues?.[key]?.value
          : modValues?.[key];
      if (reqValue?.length > 0) {
        errorObj[key] = "";
        if (key === "email") {
          if (!EmailRegex.test(reqValue)) {
            errorObj[key] = "Invalid email";
          }
        }
        if (key === "password") {
          if (reqValue?.length < 6) {
            errorObj[key] = "Password should be at least 6 characters long";
          }
        }
        if (key === "mobile") {
          if (!numberRegx.test(reqValue)) {
            errorObj[key] = "Invalid mobile number";
          } else {
            if (reqValue?.length < 6) {
              errorObj[key] =
                "Mobile number should be at least 6 characters long";
            }
          }
        }
      } else {
        errorObj[key] = "This is required field";
        if (key === "password") {
          if (selectedItem) errorObj[key] = "";
        }
        if (key === "state") {
          errorObj[key] =
            getStatesBasedOnCountry(modValues?.country?.value)?.length > 0
              ? "This is required field"
              : "";
        }
      }
    });
    setModErrors(errorObj);
    return Object.values(errorObj)?.some((val) => val?.length > 0);
  };

  const onSave = () => {
    if (isNotValidValues()) return;
    const data = {
      branchName: modValues?.branchName,
      country: `${modValues?.country?.value}(${modValues?.countryCode})`,
      // countryCode: modValues?.countryCode,
      state: modValues?.state?.value ?? null,
      address: modValues?.address,
      adminContact: modValues?.adminContact,
      mobile: modValues?.mobile,
      email: modValues?.email,
      // password: modValues?.password,
      branchNumber: selectedItem
        ? selectedItem?.branchNumber
        : (branches?.length ?? 0) + 1,
      // city: "Mumbai",
    };
    if (!selectedItem) data.password = modValues?.password;
    if (selectedItem) data.id = selectedItem?.id;
    dispatch(
      createUpdateClinicBranch({
        data,
        successMsg: selectedItem
          ? "Branch updated successfully"
          : "Branch created successfully",
      }),
    ).then((res) => {
      if (res?.payload) {
        refreshBranchesAndPatchMandate();
        onCloseModal();
      }
    });
  };

  return (
    <div>
      <Grid container>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          className="mh20 mb20"
        >
          <div className="flex1-end mb20">
            <div>
              <CustomButton
                text="Create Branch"
                smallBtn
                onClick={onCreateBranch}
              />
            </div>
          </div>
          <TableContainer sx={{ maxHeight: 450 }}>
            <Table
              sx={{ minWidth: 200 }}
              aria-labelledby="tableTitle"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  {tableHeaders?.map((hd) => (
                    <TableCell
                      key={getKey()}
                      style={tblGrayBack}
                      className="txt-semi-bold fs12"
                    >
                      {tableNameExpan?.[hd]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {getTableData()?.map((li) => (
                  <TableRow key={getKey()}>
                    {tableHeaders?.map((hdr) => (
                      <TableCell className={`txt-regular`}>
                        <div
                          style={{
                            width:
                              hdr === "branchName"
                                ? 200
                                : hdr === "location"
                                  ? 250
                                  : 100,
                          }}
                        >
                          {hdr === "branchName" ? (
                            <div className="flex-row-ali-cen">
                              {li?.[hdr]}
                              {!li?.branchNumber ? (
                                <Typography
                                  className={`white-color fs12 con-fit card-con-blue-back ph10 br10 ml5`}
                                >
                                  Main
                                </Typography>
                              ) : null}
                            </div>
                          ) : (
                            li?.[hdr]
                          )}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <CustomModal
        open={modVisible}
        onClose={onCloseModal}
        header={`${selectedItem ? "Edit" : "Create"} Branch`}
        rightModal
        modalWidth={30}
      >
        <Grid container spacing={2} className="ph20 scroll-90vh">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <CustomTextField
              name="branchName"
              label="Branch Name"
              fullWidth
              value={modValues?.branchName}
              helperText={modErrors?.branchName}
              error={!!modErrors?.branchName}
              handleChange={(e) => handleChange("branchName", e?.target?.value)}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <CustomSelect
              list={countryList}
              value={modValues?.country}
              handleChange={(e, value) => handleChange("country", value)}
              required
              label="Country"
              search
              error={!!modErrors?.country}
              helperText={modErrors?.country}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <CustomSelect
              list={getStatesBasedOnCountry(modValues?.country?.value)}
              value={modValues?.state}
              handleChange={(e, value) => handleChange("state", value)}
              required
              label="State"
              search
              error={!!modErrors?.state}
              helperText={modErrors?.state}
              disabled={
                getStatesBasedOnCountry(modValues?.country?.value)?.length === 0
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <CustomTextField
              name="address"
              label="Address"
              fullWidth
              value={modValues?.address}
              helperText={modErrors?.address}
              error={!!modErrors?.address}
              labelTop
              handleChange={(e) => handleChange("address", e?.target?.value)}
              multiline
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <CustomTextField
              name="adminContact"
              label="Administrator Name"
              fullWidth
              value={modValues?.adminContact}
              helperText={modErrors?.adminContact}
              error={!!modErrors?.adminContact}
              handleChange={(e) =>
                handleChange("adminContact", e?.target?.value)
              }
              // prefix={modValues?.countryCode ?? ""}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <CustomTextField
              name="mobile"
              label="Contact Number"
              fullWidth
              value={modValues?.mobile}
              helperText={modErrors?.mobile}
              error={!!modErrors?.mobile}
              handleChange={(e) => handleChange("mobile", e?.target?.value)}
              prefix={modValues?.countryCode ?? ""}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <CustomTextField
              name="email"
              label="Email"
              fullWidth
              value={modValues?.email}
              helperText={modErrors?.email}
              error={!!modErrors?.email}
              handleChange={(e) => handleChange("email", e?.target?.value)}
            />
          </Grid>
          {selectedItem ? null : (
            <>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomTextField
                  name="password"
                  fullWidth
                  handleChange={(e) =>
                    handleChange("password", e?.target?.value)
                  }
                  value={modValues?.password}
                  helperText={modErrors?.password}
                  error={!!modErrors?.password}
                  label="Password"
                  showPassword={showPassword}
                  handleClickShowPassword={() => setShowPassword(!showPassword)}
                  password
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomCheckbox
                  label="Auto-Generate Password"
                  checked={modValues?.isAutoGenPass}
                  onChange={() =>
                    handleChange("isAutoGenPass", !modValues?.isAutoGenPass)
                  }
                />
              </Grid>
            </>
          )}
          <div className="flex1-center mv20">
            <div>
              <CustomButton text="Save" smallBtn onClick={onSave} />
            </div>
          </div>
        </Grid>
      </CustomModal>
    </div>
  );
};

export default ClinicBranches;
